import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmt, fmtDate } from '../api';
import { Loading, ErrorBox, Empty } from '../ui';
import { Icons } from '../icons';

/**
 * Prixod tarixi — QATOR: mahsulot, USTUN: yuk (rastamojka) sanasi.
 * Katakda o'sha yukdagi «Стоимость приходда».
 *
 * Rahbar bitta guruhni to'liq ko'rishi uchun: katalogda mahsulotni bittalab
 * ochish o'rniga, «Panel Akril» ni tanlaydi va hammasi bir ekranda chiqadi.
 * Har qatorning eng qimmat kataki ajratib ko'rsatiladi — shartnoma narxi
 * aynan o'shandan hisoblanadi.
 */
export default function PrixodMatrix() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [brand, setBrand] = useState('');
  const [lastN, setLastN] = useState('8');
  const reqId = useRef(0);

  const qs = () => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (group) p.set('group', group);
    if (brand) p.set('brand', brand);
    if (lastN) p.set('lastN', lastN);
    return p.toString() ? '?' + p.toString() : '';
  };

  const load = () => {
    setErr('');
    const my = ++reqId.current;
    api.get('/prixod-matrix' + qs())
      .then((r) => { if (my === reqId.current) setD(r); })
      .catch((e) => { if (my === reqId.current) setErr(e.message); });
  };
  useEffect(() => { load(); }, [group, brand, lastN]);

  const download = () => api.download('/prixod-matrix/export' + qs().replace(/lastN=\d+&?/, ''), 'prixod-matritsa.xlsx');

  if (err) return <ErrorBox text={err} onRetry={load} />;
  if (!d) return <Loading />;

  const cols = d.columns || [];
  const rows = d.rows || [];

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Prixod tarixi</h1>
          <p className="page-sub">
            Har yukdagi <b>Стоимость приходда</b> (so'mda). Qatorning <b>eng qimmat</b> kataki ajratilgan —
            <b> Prixod narxi</b> va shartnoma narxi aynan o'shandan hisoblanadi.
            Yangi yuk kirgach ustun o'zi qo'shiladi.
          </p>
        </div>
        <button className="btn" onClick={download}><Icons.download size={17} /> Excel</button>
      </div>

      <form className="filters" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <select className="input" style={{ width: 230 }} value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="">Barcha rastamojka guruhlari</option>
          {(d.filters?.groups || []).map((g: string) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="input" style={{ width: 150 }} value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">Barcha brendlar</option>
          {(d.filters?.brands || []).map((b: string) => <option key={b} value={b}>{b}</option>)}
        </select>
        <input className="search" placeholder="Artikul / nom" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ width: 165 }} value={lastN} onChange={(e) => setLastN(e.target.value)} title="Ekranga sig'diradi">
          <option value="5">Oxirgi 5 ta yuk</option>
          <option value="8">Oxirgi 8 ta yuk</option>
          <option value="12">Oxirgi 12 ta yuk</option>
          <option value="0">Barcha yuklar</option>
        </select>
        <button className="btn">Qidirish</button>
        {(search || group || brand) && (
          <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); setGroup(''); setBrand(''); }}>Tozalash</button>
        )}
        <span className="spacer" />
        <span className="muted">{d.totals?.products ?? 0} ta mahsulot · {cols.length} ta yuk</span>
      </form>

      {d.hiddenCols > 0 && (
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          ℹ️ Yana {d.hiddenCols} ta eskiroq yuk ko'rsatilmadi — «Barcha yuklar» ni tanlang yoki Excelga yuklang.
          <b> Eng qimmat</b> ustuni ularni ham hisobga oladi.
        </p>
      )}

      {rows.length === 0 ? (
        <Empty text="Ma'lumot yo'q" hint="Rastamojka tasdiqlanib, prixod qiymatlari kiritilgach shu yerda chiqadi" />
      ) : (
        <div className="table-wrap matrix-wrap">
          <table className="matrix">
            <thead>
              <tr>
                <th className="stick-l1">Artikul</th>
                <th className="stick-l2">Mahsulot</th>
                <th>Guruh</th>
                {cols.map((c: any) => (
                  <th key={c.id} className="num" title={`${c.number}${c.gtdNumber ? ' · GTD ' + c.gtdNumber : ''}`}>
                    {fmtDate(c.date)}
                    <div className="th-sub">{c.number}</div>
                  </th>
                ))}
                <th className="num stick-r2" title="Shu mahsulot bo'yicha eng qimmat kirim qiymati">
                  Prixod narxi<div className="th-sub">eng qimmat</div>
                </th>
                <th className="num stick-r1" title="Prixod narxi × 1,1 × 1,12">
                  Shartnoma<div className="th-sub">× 1,1 × 1,12</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.productId}>
                  <td className="stick-l1">
                    <Link to={'/products/' + r.productId} className="code" style={{ color: 'var(--accent)' }}>{r.article}</Link>
                  </td>
                  <td className="stick-l2">
                    {r.name}
                    {r.power && <span className="badge b-draft" style={{ marginLeft: 6 }}>{r.power}</span>}
                  </td>
                  <td className="muted">{r.group || '—'}</td>
                  {cols.map((c: any) => {
                    const v = r.cells?.[c.id];
                    const isMax = r.maxDeclId === c.id;
                    return (
                      <td key={c.id} className={'num' + (isMax ? ' cell-max' : '')}>
                        {v == null ? <span className="muted">·</span> : fmt(v, 2)}
                      </td>
                    );
                  })}
                  <td className="num stick-r2" style={{ fontWeight: 700 }}>
                    {r.max == null ? <span className="muted">—</span> : fmt(r.max, 2)}
                  </td>
                  <td className="num stick-r1" style={{ fontWeight: 700, color: 'var(--accent)' }}>
                    {r.contractPrice == null ? <span className="muted">—</span> : fmt(r.contractPrice, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
